import { useCallback } from "react";
import type { CampaignMetrics } from "@/hooks/api/use-campaign-metrics";
import { buildFilterGroupFromColumnState } from "@/lib/filters";
import type {
  CampaignListFilters,
  CampaignsQueryParams,
} from "@/types/campaign-types";
import type { UnifiedTableColumn } from "@/types/table-column";
import type { Campaign } from "./api/use-campaigns";
import { type BaseListFilters, useListFilters } from "./use-list-filters";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Default sort settings
const DEFAULT_SORT_COLUMN = "start_date";
const DEFAULT_SORT_DIRECTION = "desc";

// SessionStorage keys
const CAMPAIGN_LIST_FILTERS_KEY = "campaigns::list::filters";
const CAMPAIGN_METRICS_KEY = "campaigns::list::metrics";

const DEFAULT_METRICS: Array<keyof CampaignMetrics> = [
  "accounts_engaged",
  "total_engagements",
  "people_engaged",
];

// Extend base list filters with campaign-specific filters
interface ExtendedCampaignListFilters
  extends BaseListFilters,
    CampaignListFilters {}

/**
 * Hook for managing campaign metrics
 * @returns Methods for getting and setting selected metrics
 */
export function useCampaignMetrics() {
  // Get and set metrics using sessionStorage for tab-specific behavior
  const [selectedMetrics, setSelectedMetrics] = useTrackedSessionStorage<
    Array<keyof CampaignMetrics>
  >(CAMPAIGN_METRICS_KEY, DEFAULT_METRICS);

  return {
    selectedMetrics,
    setSelectedMetrics,
    getDefaultMetrics: () => DEFAULT_METRICS,
  };
}

/**
 * Hook for managing campaign list filters
 * @returns Filters and utilities for managing campaign list filters
 */
export function useCampaignListFilters() {
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams: getBaseQueryParams,
  } = useListFilters<
    ExtendedCampaignListFilters,
    UnifiedTableColumn<Campaign>,
    CampaignsQueryParams
  >({
    storageKey: CAMPAIGN_LIST_FILTERS_KEY,
    defaultSortColumn: DEFAULT_SORT_COLUMN,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    buildQueryParams,
  });

  // Helper function to build query params
  function buildQueryParams(
    filters: ExtendedCampaignListFilters,
    options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<Campaign>[];
      limit?: number;
    },
  ): CampaignsQueryParams {
    const { searchTerm, columns = [], limit = 25 } = options || {};

    // Sort handling – follow same logic as accounts list
    let sortCol = filters.sortColumn || DEFAULT_SORT_COLUMN;

    // Fallback if sort column no longer exists in current column set
    const validSortKeys = columns.map((c) => c.key);
    if (!validSortKeys.includes(sortCol)) {
      sortCol = DEFAULT_SORT_COLUMN;
    }

    const sortDir = filters.sortDirection || DEFAULT_SORT_DIRECTION;

    // Find column definition to get null_sort behaviour
    const column = columns.find((col) => col.key === sortCol);

    const params: CampaignsQueryParams = {
      sort_by: sortCol,
      sort_direction: sortDir,
      null_sort: column?.nullSort,
      search: searchTerm || filters.search || undefined,
      limit,
    };

    // Extract formulas from columns
    const formulaColumns = columns.filter(
      (col) => col.isFormulaColumn && col.formulaDefinition,
    );
    if (formulaColumns.length > 0) {
      params.formulas = formulaColumns.map((col) => col.formulaDefinition!);
    }

    // Structured filters JSON
    const validColumnKeys = new Set(columns.map((c) => c.key.toString()));
    const group = buildFilterGroupFromColumnState(filters, validColumnKeys);
    if (group) params.filters = JSON.stringify(group);

    return params;
  }

  // Wrapper for getQueryParams that accepts metrics and filters
  const getQueryParams = useCallback(
    (options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<Campaign>[];
      limit?: number;
      metrics?: Array<keyof CampaignMetrics>;
      metric_filters?: CampaignsQueryParams["metric_filters"];
      formulas?: CampaignsQueryParams["formulas"];
    }) => {
      const { metrics, metric_filters, formulas, ...restOptions } =
        options || {};

      // Get base query params from filters
      const params = getBaseQueryParams(restOptions) as CampaignsQueryParams;

      // Add metrics and metric_filters if provided
      if (metrics) {
        params.metrics = metrics;
      }

      if (metric_filters) {
        params.metric_filters = metric_filters;
      }

      // Add formulas if provided
      if (formulas) {
        params.formulas = formulas;
      }

      return params;
    },
    [getBaseQueryParams],
  );

  return {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
    getDefaultMetrics: () => DEFAULT_METRICS,
  };
}
